'use client';

import { Badge } from '@/lib/components/ui/badge';
import { Button } from '@/lib/components/ui/button';
import { Card, CardContent, CardFooter } from '@/lib/components/ui/card';
import { Eye } from 'lucide-react';
import { formatCurrency } from '@/lib/helpers/strings';
import { Matching } from '../interfaces';
import router from 'next/router';
import dayjs from 'dayjs';
import HeadHunterAvatar from './HeadHunterAvatar';
import { MdCancel } from 'react-icons/md';

import { track } from '@/lib/analytics/client';

import { gql, useMutation } from 'modules/GraphQL';
import classNames from 'classnames';

interface MatchingCardProps {
  matching: Matching;
  type: 'availableProfile' | 'searchedProfile';
  action?: 'propose' | 'view';
  onPropose?: () => void;
  onStatusChange?: () => void;
}

const VIEW_MATCHING = gql`
  mutation viewMatching($_id: ID!) {
    viewMatching(_id: $_id) {
      item {
        _id
      }
    }
  }
`;

const REJECT_MATCHING = gql`
  mutation rejectMatching($_id: ID!) {
    rejectMatching(_id: $_id) {
      item {
        _id
      }
    }
  }
`;

function MatchingCard({ matching, type, action = 'view', onPropose, onStatusChange }: MatchingCardProps) {
  const seen = !!matching.viewedAt;
  const item = matching[type] as NonNullable<(typeof matching)[typeof type]>;

  const trackEvent = (event: string) => {
    const trackingName = `${type === 'availableProfile' ? 'available_profiles' : 'searched_profiles'}.${action === 'view' ? '.mine' : ''}`;
    const trackProperties = {
      matchingId: matching._id,
      ...(type === 'availableProfile' ? { availableProfileId: item._id } : { searchedProfileId: item._id }),
      name: item.name,
      sectorIds: item.sectorIds,
      occupationIds: (item as any).occupationIds || [(item as any).occupationId],
      countryIds: item.countryIds,
    };

    track(`${trackingName}.${event}`, trackProperties);
  };

  const [viewMatching, { loading: viewMatchingLoading }] = useMutation(VIEW_MATCHING, {
    onCompleted: () => {
      onStatusChange?.();
    },
  });
  const [rejectMatching, { loading: rejectMatchingLoading }] = useMutation(REJECT_MATCHING, {
    onCompleted: () => {
      onStatusChange?.();
    },
  });

  const publishedDate = item.publishedAt;
  const timeAgo = dayjs(publishedDate).fromNow();
  const perfectMatching =
    type === 'availableProfile'
      ? matching.availableProfile && matching.fromSearchedProfile
      : matching.searchedProfile && matching.fromAvailableProfile;

  const handleRejectMatching = (e: any) => {
    e.preventDefault();
    e.stopPropagation();
    trackEvent('reject.matching');
    rejectMatching({ variables: { _id: matching._id } });
  };

  const goToItem = (e: any) => {
    e.preventDefault();
    e.stopPropagation();
    viewMatching({ variables: { _id: matching._id } });

    trackEvent('view.matching');
    if (action === 'view') {
      if (type === 'availableProfile') {
        router.push(`/available-profiles/${item._id}?from=matching`);
      } else {
        router.push(`/searched-profiles/${item._id}?from=matching`);
      }
    } else {
      onPropose?.();
    }
  };

  return (
    <Card
      className={classNames(`w-full max-w-md round-sm border bg-white`, {
        'opacity-50 text-gray-500': seen || rejectMatchingLoading,
        ' hover:opacity-100 hover:text-inherit': seen,
      })}
    >
      <CardContent className="p-4">
        <div className="flex justify-between items-start gap-2">
          <div className="flex-1">
            <h3 className="font-semibold text-lg line-clamp-2">{item.name}</h3>
            <div className=" flex items-center gap-2">
              <span className="text-xs text-muted-foreground">{timeAgo}</span>
              {perfectMatching ? (
                <Badge className="badge-sm" variant="default">
                  Matching 🎉
                </Badge>
              ) : (
                <Badge className="badge-sm" variant="ghost">
                  Matching 👍
                </Badge>
              )}
            </div>
          </div>
          <div>
            <span className="text-lg font-bold text-primary">{formatCurrency({ value: item.bounty, currency: item.currency })}</span>
          </div>
        </div>
        {item.createdBy && (
          <div className="flex justify-start my-2 leading-none gap-2 text-muted-foreground items-center text-xs">
            <span className="text-gray-500">Proposé par</span>
            <HeadHunterAvatar user={item.createdBy} withCompany={false} />
          </div>
        )}
        <hr />
        <div className="mt-3 flex items-center justify-between text-sm">
          <div className="flex items-center gap-1">
            <span className="text-muted-foreground">Expérience:</span>
            <span className="font-medium">{item.experience} ans</span>
          </div>
          <div className="flex items-center gap-1">
            <span className="text-muted-foreground">Salaire:</span>
            <span className="font-medium">
              {formatCurrency({ value: item.baseSalary.min, currency: item.currency })} -{' '}
              {formatCurrency({ value: item.baseSalary.max, currency: item.currency })}
            </span>
          </div>
        </div>
      </CardContent>

      <CardFooter className="flex justify-between p-4 pt-0">
        <Button variant="outline" size="sm" onClick={goToItem} className="flex items-center gap-1 hover:bg-gray-100" loading={viewMatchingLoading}>
          <Eye className="h-4 w-4" />
          {action === 'view'
            ? type === 'availableProfile'
              ? seen
                ? 'Revoir le candidat Off-Market'
                : 'Voir le candidat Off-Market'
              : seen
              ? 'Revoir la mission'
              : 'Voir la mission'
            : type === 'availableProfile'
            ? 'Propose ce candidat'
            : 'Propose cette mission'}
        </Button>

        <Button variant="destructive" size="sm" onClick={handleRejectMatching} loading={rejectMatchingLoading}>
          <MdCancel />
          Pas intéressé
        </Button>
      </CardFooter>
    </Card>
  );
}

export default MatchingCard;
